// Copyright (c) Subzero Labs, Inc.
// SPDX-License-Identifier: Apache-2.0

import type { Wallet, WalletAccount } from "@wallet-standard/base";
import type { StandardEventsFeature } from "@wallet-standard/features";
import type { FrostConfig } from "../config";

type WalletWithEvents = Wallet & {
  features: {
    "standard:events"?: StandardEventsFeature["standard:events"];
  };
};

interface WalletChangeEvent {
  accounts?: readonly WalletAccount[];
}

export class WalletEventBridge {
  private readonly config: FrostConfig;
  private readonly subscriptions = new Map<string, () => void>();

  public constructor(config: FrostConfig) {
    this.config = config;
  }

  public subscribeToWallet(wallet: Wallet): void {
    const walletWithEvents = wallet as WalletWithEvents;
    const eventsFeature = walletWithEvents.features["standard:events"];
    if (!eventsFeature) return;

    const unsubscribe = eventsFeature.on("change", (event: WalletChangeEvent) => {
      // Handle account changes
      if (event.accounts) {
        this.handleAccountChange(wallet.name, event.accounts);
      }
    });

    this.subscriptions.set(wallet.name, unsubscribe);
  }

  public unsubscribeFromWallet(walletName: string): void {
    const unsubscribe = this.subscriptions.get(walletName);
    if (unsubscribe) {
      unsubscribe();
      this.subscriptions.delete(walletName);
    }
  }

  private handleAccountChange(walletName: string, accounts: readonly WalletAccount[]): void {
    const state = this.config.store.state;

    // Only process if this is our connected wallet
    if (state.walletName !== walletName) return;

    if (accounts.length === 0) {
      // All accounts removed - disconnect
      this.config.store.setState(s => ({
        ...s,
        status: "disconnected",
        walletName: null,
        accountAddress: null,
        connectedAt: null,
        sessionExpiresAt: null,
      }));
      return;
    }

    // Update to new account (single account model)
    const newAccount = accounts[0];

    this.config.store.setState(s => {
      const accountsMap = new Map(s.accounts);
      // Include the raw WalletAccount reference for wallet-standard operations
      accountsMap.set(newAccount.address, {
        address: newAccount.address,
        publicKey: new Uint8Array(newAccount.publicKey),
        walletName,
        chains: [...newAccount.chains],
        _raw: newAccount,
      });

      return {
        ...s,
        accountAddress: newAccount.address,
        lastUsedAt: Date.now(),
        accounts: accountsMap,
      };
    });
  }

  public destroy(): void {
    for (const unsubscribe of this.subscriptions.values()) {
      unsubscribe();
    }
    this.subscriptions.clear();
  }
}
